import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
   Wallet,
  TrendingUp,
  ShoppingBag,
  Package,
  RefreshCw,
  Calendar,
  Truck,
  Receipt,
  BadgeDollarSign,
  DollarSign,
  Loader2,
} from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";



import PageWrapper from "../components/PageWrapper";
import { apiFetch } from "../utils/apiFetch";

const COLORS = [
  "#6366f1",
  "#3b82f6",
  "#06b6d4",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
];

export default function SettlementDashboard() {
  const [data, setData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const [loading, setLoading] = useState(false);
const [searchOrderId, setSearchOrderId] = useState("");

const [summaryGlobal, setSummaryGlobal] = useState(null);
const [page, setPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [limit] = useState(50); // Set data per halaman

  const [activeType, setActiveType] = useState(null);

  const filteredData = useMemo(() => {
  if (!searchOrderId) return data;

  return data.filter((item) =>
    String(item.order_id || item.order_status ||item.biensi_type ||item.statement_type || item.trx_type || item.nama_toko || "")
      .toLowerCase()
      .includes(searchOrderId.toLowerCase())
  );
}, [data, searchOrderId]);

  const getDefaultDateRange = () => {
  const today = new Date();

  const firstDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );

  const format = (date) =>
    date.toISOString().split("T")[0];

  return {
    from: format(firstDay),
    to: format(today),
  };
};

const defaultDate = getDefaultDateRange();

const [dateFrom, setDateFrom] = useState('');

const [dateTo, setDateTo] = useState('');

  const formatRupiah = (num) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(num || 0);

 const fetchData = async (targetPage = page) => {
  try {
    setLoading(true);
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();
    params.append("date_from", dateFrom);
    params.append("date_to", dateTo);
    params.append("page", targetPage);
    params.append("limit", limit);

    const res = await apiFetch(
      `/api/tiktokshop/settlement-dashboard-global?${params.toString()}`,
      {
        headers: {
          "x-api-key": "SECRET123456",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const json = await res.json();
    if (json.success) {
      setData(json.data || []);
      setSummaryGlobal(json.summary); // Menyimpan summary dari DB
      setTotalPages(json.meta.total_pages);
      setPage(json.meta.page);
    }
  } catch (err) {
    console.log(err);
  } finally {
    setLoading(false);
  }
};

// Pastikan useEffect memicu fetch ketika page berganti
useEffect(() => {
  if (dateFrom && dateTo) {
    fetchData(page);
  }
}, [dateFrom, dateTo, page]);

// Sesuaikan hooks useMemo `summary` Anda untuk membaca data global jika ada
const summary = useMemo(() => {
  if (summaryGlobal) return summaryGlobal;
  
  // fallback jika data belum termuat
  return {
    totalBFD: 0, totalPayment: 0, totalWithdraw: 0, totalRevenue: 0,
    totalSettlement: 0, totalQty: 0, totalShippingCost: 0, totalFeeTax: 0,
    totalCost: 0, totalOrder: 0
  };
}, [summaryGlobal]);


// Tambahkan parameter 'rawData' di fungsi ini
const exportToExcel = (rawData) => {
  if (!rawData || rawData.length === 0) {
    alert("Tidak ada data untuk diexport");
    return;
  }

  const excelData = rawData.map((item) => {
    const totalCost =
      Math.abs(Number(item.shipping_cost_amount || 0)) +
      Math.abs(Number(item.fee_tax_amount || 0));

    const bfd = Number(item.subtotal_before_discount_amount || 0);
    const costPercent = bfd > 0 ? (totalCost / bfd) * 100 : 0;

    return {
      "Create Time": item.create_time ? item.create_time.replace("T", " ").substring(0, 19) : "",
      "Nama Toko": item.nama_toko || "-",
      "Order ID": item.order_id,
      "Line Item ID": item.line_item_id || "-",
      "Type Process": item.biensi_type,
      "Type Sales": item.sales_type,
      "Status": item.order_status,
      "SKU": item.seller_sku,
      "Qty": Number(item.quantity || 0),
      "Tarik Dana": Number(item.withdraw_amount || 0),
      "Payment": Number(item.payment_amount || 0),
      "Subtotal BFD": bfd,
      "Revenue": Number(item.rv_per_sku || 0),
      "Settlement": Number(item.settle_per_sku || 0),
      "Shipping Cost":Number(item.shipping_cost_amount || 0),
      "Customer Paid Shipping Fee": Number(item.customer_paid_shipping_fee_amount || 0),
      "Shipping Fee Discount": Number(item.shipping_fee_discount_amount || 0),
      "Return Shipping Fee": Number(item.return_shipping_fee_amount || 0),
      "Exchange Shipping Fee": Number(item.exchange_shipping_fee_amount || 0),
      "Actual Shipping Fee": Number(item.actual_shipping_fee_amount || 0),
      "COD Service Fee": Number(item.cod_service_fee_amount || 0),
      "Refund COD Service Fee": Number(item.refund_cod_service_fee_amount || 0),
      "Logistics Service Fee": Number(item.logistics_service_fee || 0),
      "Fee Tax Amount": Number(item.fee_tax_amount || 0),
      "Seller Discount": Number(item.seller_discount_amount || 0),
      "pre_order_service_fee_amount": Number(item.pre_order_service_fee_amount || 0),
       "gmv_max_ad_fee_amount": Number(item.gmv_max_ad_fee_amount || 0),
      "Voucher Xtra Service Fee": Number(item.voucher_xtra_service_fee_amount || 0),
      "Flash Sales Service Fee": Number(item.flash_sales_service_fee_amount || 0),
      "Bonus Cashback Service Fee": Number(item.bonus_cashback_service_fee_amount || 0),
      "Live Specials Fee": Number(item.live_specials_fee_amount || 0),
      "Affiliate Commission": Number(item.affiliate_commission_amount || 0),
      "Platform Commission": Number(item.platform_commission_amount || 0),
      "Refund Administration Fee": Number(item.refund_administration_fee_amount || 0),
      "Transaction Fee": Number(item.transaction_fee_amount || 0),
      "Credit Card Handling Fee": Number(item.credit_card_handling_fee_amount || 0),
      "Affiliate Ads Commission": Number(item.affiliate_ads_commission_amount || 0),
      "Cofunded Promotion Service Fee": Number(item.cofunded_promotion_service_fee_amount || 0),
      "Dynamic Commission": Number(item.dynamic_commission_amount || 0),
      "External Affiliate Marketing Fee": Number(item.external_affiliate_marketing_fee_amount || 0),
      "VN Fix Infrastructure Fee": Number(item.vn_fix_infrastructure_fee || 0),
      "Tap Shop Ads Commission": Number(item.tap_shop_ads_commission || 0),
      "Total Cost": totalCost,
      "Cost %": costPercent / 100,
    };
  });

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Dapatkan total kolom dinamis berdasarkan jumlah key di objek data
  const totalColsCount = Object.keys(excelData[0]).length;
  const lastColumnLetter = XLSX.utils.encode_col(totalColsCount - 1);

  // AUTO FILTER mengikuti dinamisnya kolom
  worksheet["!autofilter"] = {
    ref: `A1:${lastColumnLetter}${excelData.length + 1}`,
  };

  // FREEZE HEADER
  worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

  // Set lebar kolom secara otomatis (default ke panjang nama header)
  worksheet["!cols"] = Object.keys(excelData[0]).map(key => ({
    wch: Math.max(key.length + 4, 12)
  }));

  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  // HEADER STYLE
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (cell) {
      cell.s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F46E5" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "D1D5DB" } },
          bottom: { style: "thin", color: { rgb: "D1D5DB" } },
          left: { style: "thin", color: { rgb: "D1D5DB" } },
          right: { style: "thin", color: { rgb: "D1D5DB" } },
        },
      };
    }
  }

  // BODY STYLE & FORMATTING
  for (let R = 1; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
      if (!cell) continue;

      cell.s = {
        alignment: {
          vertical: "center",
          horizontal: typeof cell.v === "number" ? "right" : "left",
        },
        border: {
          top: { style: "thin", color: { rgb: "E5E7EB" } },
          bottom: { style: "thin", color: { rgb: "E5E7EB" } },
          left: { style: "thin", color: { rgb: "E5E7EB" } },
          right: { style: "thin", color: { rgb: "E5E7EB" } },
        },
        fill: {
          fgColor: { rgb: R % 2 === 0 ? "F9FAFB" : "FFFFFF" },
        },
      };

      // FORMAT RUPIAH OTOMATIS: 
      // Karena indeks kolom bertambah, format rupiah disematkan pada seluruh kolom 
      // yang berisikan data angka (indeks ke-9 sampai kolom sebelum terakhir)
      if (C >= 9 && C < (totalColsCount - 1)) {
        cell.z = '"Rp"#,##0;[Red]-"Rp"#,##0';
      }

      // FORMAT PERCENT (Kolom paling terakhir / "Cost %")
      if (C === (totalColsCount - 1)) {
        cell.z = "0.00%";
      }
    }
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, "Settlement");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const fileData = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(fileData, `Settlement-${dateFrom}-${dateTo}.xlsx`);
};


const handleExportButtonClick = async () => {
  try {
    // Sediakan state loading global agar user tahu aplikasi sedang memproses ribuan data
    setExportLoading(true); 

    // Panggil endpoint baru backend yang tanpa limit/page
    const response = await axios.get("/api/tiktokshop/settlement/export-all", {
      params: {
        date_from: dateFrom,
        date_to: dateTo
      }
    });

    if (response.data.success) {
      // Jalankan fungsi cetak excel dengan menyuplai seluruh baris data dari database
      exportToExcel(response.data.data);
    } else {
      alert("Gagal memuat data: " + response.data.message);
    }
  } catch (error) {
    console.error(error);
    alert("Terjadi kesalahan sistem saat mengeksport data.");
  } finally {
    setExportLoading(false);
  }
};


  const chartData = useMemo(() => {
    const grouped = {};

    data.forEach((item) => {
      const date = item.create_time.replace("T", " ").substring(0, 19);

      if (!grouped[date]) {
        grouped[date] = {
          date,
          payment: 0,
          revenue: 0,
          settlement: 0,
        };
      }

      grouped[date].payment += Number(
        item.settle_per_order || 0
      );

      grouped[date].revenue += Number(
        item.rv_per_order || 0
      );

      grouped[date].settlement += Number(
        item.settle_per_order || 0
      );
    });

    return Object.values(grouped);
  }, [data]);

  const typeData = useMemo(() => {
    const grouped = {};

    data.forEach((item) => {
      const type = item.order_status || "UNKNOWN";

      if (!grouped[type]) {
        grouped[type] = {
          name: type,
          value: 0,
        };
      }

      grouped[type].value += Number(
        item.settle_per_order || 0
      );
    });

    return Object.values(grouped);
  }, [data]);

const stats = [
  {
    label: "Tarik Dana",
    description:
      "Total dana yang berhasil ditarik ke rekening",
    value: formatRupiah(summary.totalWithdraw),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalWithdraw /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Wallet,
    color: "from-emerald-500 to-green-600",
    bg: "bg-emerald-50",
  },

  {
    label: "Total Payment",
    description:
      "Total pembayaran TikTok ke seller",
    value: formatRupiah(summary.totalPayment),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalPayment /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Wallet,
    color: "from-indigo-500 to-blue-500",
    bg: "bg-indigo-50",
  },

  {
    label: "Subtotal BFD",
    description:
      "Nilai bruto produk sebelum diskon",
    value: formatRupiah(summary.totalBFD),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalBFD /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: DollarSign,
    color: "from-violet-500 to-fuchsia-500",
    bg: "bg-violet-50",
  },

  {
    label: "Revenue",
    description:
      "Total pendapatan bersih transaksi",
    value: formatRupiah(summary.totalRevenue),
    percent: "100%",
    icon: TrendingUp,
    color: "from-green-500 to-emerald-500",
    bg: "bg-green-50",
  },

  {
    label: "Settlement",
    description:
      "Total settlement order yang diterima",
    value: formatRupiah(summary.totalSettlement),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalSettlement /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: ShoppingBag,
    color: "from-orange-500 to-yellow-500",
    bg: "bg-orange-50",
  },

  {
    label: "Shipping Cost",
    description:
      "Total biaya pengiriman",
    value: formatRupiah(summary.totalShippingCost),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalShippingCost /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Truck,
    color: "from-cyan-500 to-sky-500",
    bg: "bg-cyan-50",
  },

  {
    label: "Fee Tax",
    description:
      "Total fee platform & pajak",
    value: formatRupiah(summary.totalFeeTax),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalFeeTax /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Receipt,
    color: "from-red-500 to-rose-500",
    bg: "bg-rose-50",
  },

  {
    label: "Total Cost",
    description:
      "Akumulasi seluruh potongan biaya",
    value: formatRupiah(summary.totalCost),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalCost /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: BadgeDollarSign,
    color: "from-amber-500 to-orange-500",
    bg: "bg-amber-50",
  },
];

  return (
    <PageWrapper>
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">

        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            TikTok Settlement Finance
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Monitor settlement, order & revenue performance
          </p>
        </div>

        {/* FILTER */}
        <div className="flex flex-col sm:flex-row gap-3">

          <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
            <Calendar size={16} className="text-gray-400" />

            <input
              type="date"
              value={dateFrom}
              onChange={(e) =>
                setDateFrom(e.target.value)
              }
              className="outline-none text-sm"
            />
          </div>

          <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
            <Calendar size={16} className="text-gray-400" />

            <input
              type="date"
              value={dateTo}
              onChange={(e) =>
                setDateTo(e.target.value)
              }
              className="outline-none text-sm"
            />
          </div>

          <button
  onClick={fetchData}
  disabled={loading}
  className={`
    px-5 py-2 rounded-xl
    text-white text-sm font-medium
    transition flex items-center gap-2
    ${
      loading
        ? "bg-indigo-400 cursor-not-allowed"
        : "bg-indigo-600 hover:bg-indigo-700"
    }
  `}
>
  <RefreshCw
    size={16}
    className={loading ? "animate-spin" : ""}
  />

  {loading ? "Loading..." : "Filter"}
</button>

          <button
  onClick={handleExportButtonClick}
  disabled={loading || data.length === 0}
  className={`
    px-5 py-2 rounded-xl
    text-white text-sm font-medium
    transition flex items-center gap-2
    ${
      loading || data.length === 0
        ? "bg-green-400 cursor-not-allowed"
        : "bg-green-600 hover:bg-green-700"
    }
  `}
>
  Export Excel
</button>

        </div>
      </div>

      {/* STATS */}
     <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8 gap-5">

  {loading
    ? Array.from({ length: 8 }).map((_, i) => (
       <div
  key={i}
  className="
    bg-white rounded-3xl
    p-5 border border-gray-100
    shadow-sm hover:shadow-lg
    transition-all duration-300
    hover:-translate-y-1
  "
>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="h-3 w-20 bg-gray-200 rounded mb-3" />

              <div className="h-6 w-28 bg-gray-300 rounded" />
            </div>

            <div className="w-12 h-12 rounded-xl bg-gray-200" />
          </div>
        </div>
      ))

    : stats.map((s, i) => {
        const Icon = s.icon;

        return (
          <div
            key={i}
            className="bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition"
          >
           <div className="flex items-start justify-between gap-3">

  <div className="flex-1 min-w-0">

    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
      {s.label}
    </p>

    <h2 className="text-lg font-bold text-gray-800 mt-2 leading-tight">
      {s.value}
    </h2>

    {s.percent && (
      <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
        <span className="text-[11px] font-semibold text-gray-700">
          {s.percent} dari revenue
        </span>
      </div>
    )}
  </div>

  <div
    className={`
      w-10 h-10 rounded-2xl
      flex items-center justify-center
      bg-gradient-to-br ${s.color}
      text-white shadow-sm
      shrink-0
    `}
  >
    <Icon size={16} />
  </div>

</div>
          </div>
        );
      })}
</div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-6">

        {/* CHART */}
        <div className="xl:col-span-2 bg-white rounded-2xl border shadow-sm p-5">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">

            <div>
              <h3 className="font-semibold text-gray-700">
                Settlement Trend
              </h3>

              <p className="text-xs text-gray-400 mt-1">
                Daily settlement overview
              </p>
            </div>

          </div>

          <div className="relative">
  {loading && (
    <div className="absolute inset-0 z-10 bg-white/70 backdrop-blur-[1px] flex items-center justify-center rounded-xl">
      <div className="flex items-center gap-2 text-indigo-600">
        <Loader2
          size={18}
          className="animate-spin"
        />

        <span className="text-sm font-medium">
          Loading chart...
        </span>
      </div>
    </div>
  )}

          <ResponsiveContainer width="100%" height={350}>

            <AreaChart data={chartData}>

              <defs>

                <linearGradient
                  id="payment"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#6366f1"
                    stopOpacity={0.4}
                  />

                  <stop
                    offset="95%"
                    stopColor="#6366f1"
                    stopOpacity={0}
                  />
                </linearGradient>

              </defs>

              <CartesianGrid
                stroke="#f1f5f9"
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
              />

              <YAxis
                tickFormatter={(v) =>
                  `${(v / 1000000).toFixed(0)}M`
                }
                tick={{ fontSize: 11 }}
              />

              <Tooltip
                formatter={(v) =>
                  formatRupiah(v)
                }
              />

              <Area
                type="monotone"
                dataKey="payment"
                stroke="#6366f1"
                fill="url(#payment)"
                strokeWidth={3}
              />

            </AreaChart>

          </ResponsiveContainer>
</div>
        </div>

        {/* PIE */}
        <div className="bg-white rounded-2xl border shadow-sm p-5">

          <div className="flex items-center justify-between mb-5">

            <div>
              <h3 className="font-semibold text-gray-700">
                Statement Type
              </h3>

              <p className="text-xs text-gray-400 mt-1">
                Revenue contribution
              </p>
            </div>

          </div>

          <ResponsiveContainer width="100%" height={300}>

            <PieChart>

              <Pie
                data={typeData}
                dataKey="value"
                nameKey="name"
                innerRadius={70}
                outerRadius={100}
                paddingAngle={4}
                onMouseEnter={(_, index) =>
                  setActiveType(typeData[index].name)
                }
              >

                {typeData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={COLORS[i % COLORS.length]}
                    opacity={
                      activeType &&
                      activeType !== entry.name
                        ? 0.3
                        : 1
                    }
                  />
                ))}

              </Pie>

              <Tooltip
                formatter={(v) =>
                  formatRupiah(v)
                }
              />

            </PieChart>

          </ResponsiveContainer>

          <div className="space-y-2 mt-3">

            {typeData.map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between"
              >

                <div className="flex items-center gap-2">

                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      background:
                        COLORS[i % COLORS.length],
                    }}
                  />

                  <span className="text-sm text-gray-600">
                    {item.name}
                  </span>

                </div>

                <span className="text-xs text-gray-500">
                  {formatRupiah(item.value)}
                </span>

              </div>
            ))}

          </div>

        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">

        <div className="flex items-center justify-between mb-5">

          <div>
            <h3 className="font-semibold text-gray-700">
              Settlement Detail
            </h3>

            <p className="text-xs text-gray-400 mt-1">
              Detailed order settlement data
            </p>
          </div>

          <div className="relative w-full lg:w-80">

  <input
    type="text"
    placeholder="Search Order ID, Toko, Status..."
    value={searchOrderId}
    onChange={(e) =>
      setSearchOrderId(e.target.value)
    }
    className="
      w-full rounded-2xl
      border border-gray-200
      bg-gray-50
      px-4 py-3
      text-sm
      outline-none
      focus:ring-2
      focus:ring-indigo-500
      focus:border-indigo-500
      transition
    "
  />
  <p className="text-xs text-gray-400 mt-2">
  {filteredData.length.toLocaleString("id-ID")} results
</p>

</div>

        </div>

        <div className="overflow-auto">
 <table className="w-full text-sm whitespace-nowrap">
    <thead>
      <tr className="border-b bg-gray-50">
        <th className="text-left p-3 whitespace-nowrap">Tanggal WD</th>
        <th className="text-left p-3 whitespace-nowrap">Nama Toko</th>
        <th className="text-left p-3 whitespace-nowrap">Order ID</th>
        <th className="text-left p-3 whitespace-nowrap">Type</th>
        <th className="text-left p-3 whitespace-nowrap">Status</th>
        <th className="text-left p-3 whitespace-nowrap">SKU</th>
        <th className="text-right p-3 whitespace-nowrap">Qty</th>
        <th className="text-right p-3 whitespace-nowrap">Payment</th>
        <th className="text-right p-3 whitespace-nowrap">Subtotal BFD</th>
        <th className="text-right p-3 whitespace-nowrap">Seller Discount</th>
        <th className="text-right p-3 whitespace-nowrap">Revenue</th>
        <th className="text-right p-3 whitespace-nowrap">Settlement</th>
        <th className="text-right p-3 whitespace-nowrap">Shipping Cost</th>
        <th className="text-right p-3 whitespace-nowrap">Fee Tax</th>
        <th className="text-right p-3 whitespace-nowrap">Total Cost</th>
        <th className="text-right p-3 whitespace-nowrap">Cost % (vs BFD)</th>
      </tr>
    </thead>

  <tbody>
  {filteredData.map((item, i) => {
    const currentId = String(item.id).trim();

    const nextItem = data[i + 1];
    const nextId = nextItem ? String(nextItem.id).trim() : null;

    const isGroupEnd = currentId !== nextId;

    return (
      <tr
        key={`${currentId}-${i}`}
        className={`
          transition hover:bg-gray-50
          border-b
          ${isGroupEnd ? "border-b-2 border-red-500" : "border-gray-100"}
        `}
      >
        <td className="p-3 text-gray-600">
          {item.create_time?.replace("T", " ").substring(0, 19)}
        </td>

 <td className="p-3 text-gray-700 font-medium">
  {item.nama_toko || "-"}
</td>
      
        <td className="p-3 text-gray-700">{item.order_id}</td>
         <td className="p-3 text-gray-700"><span className={`text-xs font-medium px-2 py-1 rounded-md
    ${
      item.biensi_type === "P O"
        ? "bg-green-50 text-green-600"
        :item.biensi_type === "M O"
        ? "bg-yellow-50 text-yellow-600"
        : item.biensi_type === "N O"
        ? "bg-red-50 text-red-600"
        : item.biensi_type === "P R"
        ? "bg-blue-50 text-blue-600"
        :item.biensi_type === "M R"
        ? "bg-purple-50 text-purple-600"
        : item.biensi_type === "N R"
        ? "bg-orange-50 text-orange-600"
        : "bg-gray-100 text-gray-600"
    }
  `}>
    {item.biensi_type || "UNKNOWN"}
  </span>  <span className={`text-xs font-medium px-2 py-1 rounded-md
    ${
      item.sales_type === "C"
        ? "bg-green-50 text-green-600"
        :item.sales_type === "O"
        ? "bg-yellow-50 text-yellow-600"
        : "bg-gray-100 text-gray-600"
    }
  `}>
    {item.sales_type || "UNKNOWN"}
  </span></td>

        <td className="p-3">
  <span className={`text-xs font-medium px-2 py-1 rounded-md
    ${
      item.order_status === "COMPLETED"
        ? "bg-green-50 text-green-600"
        :item.order_status === "DELIVERED"
        ? "bg-green-50 text-green-600"
        : item.order_status === "CANCELLED"
        ? "bg-red-50 text-red-600"
        : item.order_status === "CANCEL"
        ? "bg-red-50 text-red-600"
        : item.order_status === "PENDING"
        ? "bg-yellow-50 text-yellow-600"
        : item.order_status === "RETURN"
        ? "bg-blue-50 text-blue-600"
        : "bg-gray-100 text-gray-600"
    }
  `}>
    {item.order_status || "UNKNOWN"}
  </span>
</td>

        <td className="p-3 text-gray-700">{item.seller_sku}</td>

        <td className="p-3 text-right">{item.quantity}</td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.payment_amount)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.subtotal_before_discount_amount)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.seller_discount_amount)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.rv_per_sku)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.settle_per_sku)}
        </td>

         <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.shipping_cost_amount)}
        </td>
         <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.fee_tax_amount)}
        </td>
          <td className="p-3 text-right text-gray-800 font-semibold">
          {formatRupiah(
              Number(item.shipping_cost_amount || 0) +
              Number(item.fee_tax_amount || 0)
          )}

        </td>
        <td className="p-3 text-right font-semibold">
  {(() => {
    const totalCost =
      Number(item.shipping_cost_amount || 0) +
      Number(item.fee_tax_amount || 0);

    const bfd = Number(item.subtotal_before_discount_amount || 0);

    const percent =
      bfd > 0 ? (totalCost / bfd) * 100 : 0;

    return (
      <span
        className={
          percent > 20
            ? "text-red-600"
            : percent > 10
            ? "text-yellow-600"
            : "text-green-600"
        }
      >
        {percent.toFixed(2)}%
      </span>
    );
  })()}
</td>

      </tr>
    );
  })}
</tbody>
  </table>

  {/* CONTROLLER PAGINATION */}
<div className="flex items-center justify-between mt-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
  <div className="text-sm text-gray-500">
    Halaman <span className="font-semibold text-gray-700">{page}</span> dari <span className="font-semibold text-gray-700">{totalPages}</span>
  </div>
  <div className="flex gap-2">
    <button
      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
      disabled={page === 1 || loading}
      className="px-4 py-2 text-sm font-medium bg-white border rounded-xl hover:bg-gray-50 disabled:opacity-50 transition"
    >
      Sebelumnya
    </button>
    <button
      onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
      disabled={page === totalPages || loading}
      className="px-4 py-2 text-sm font-medium bg-white border rounded-xl hover:bg-gray-50 disabled:opacity-50 transition"
    >
      Selanjutnya
    </button>
  </div>
</div>
</div>

      {!loading && filteredData.length === 0 && (
          <div className="text-center py-10 text-sm text-gray-400">
            No settlement data found
          </div>
        )}

      </div>
    </PageWrapper>
  );
}